import { LockClosedIcon } from '@heroicons/react/16/solid'
import Button from 'components/tailwind/Button'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../../../components/tailwind/Modal'
import { Heading } from '../../../components/ui/Heading'
import { usePartnerContext, useUserContext } from '../../../context'
import { isSampleSpace } from '../../../helpers/sample-data'
import { useSpacesByPartnerQuery } from '../../../hooks/useSpacesByPartnerQuery'
import { useTalentStorage } from '../../pricing/hooks'
import { useDiscover } from '../hooks/useDiscover'
import { useSelectionParams } from '../hooks/useSelectionParams'
import {
  hasPendingTalents,
  markLockedTalents
} from '../utils/talent-storage-lock'
import {
  SelectedTalentsList,
  TalentSearchCombobox,
  TalentSuggestionsSection
} from './talent'

export default function InviteTalentModal() {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { activeSpace } = useUserContext()

  const { inviteTalentOpen, setInviteTalentOpen } = useDiscover()
  const { addTalents } = useSelectionParams()

  const hasCompletedOnboarding = activeSpace?.hasCompletedOnboarding

  const { data: talents, isLoading: isLoadingTalents } =
    useSpacesByPartnerQuery(partner.id, {
      select: (data) =>
        data
          // freigegebene Talente nicht mehr vorschlagen
          .filter((d) => d.progress === 100 && !d.soft_deleted)
          .map((d) => ({
            id: d.id,
            progress: d.progress,
            name: (() => {
              const firstName =
                d.person?.firstname || d.person?.invitation_firstname
              const lastName =
                d.person?.lastname || d.person?.invitation_lastname

              if (firstName && lastName) {
                return `${firstName} ${lastName}`
              }

              return null
            })(),
            email: d.person?.email,
            owner: d.owner,
            createdAt: d.createdAt,
            isDemo: isSampleSpace(d)
          }))
    })

  // Die Query hier ist unmaskiert (eigener Key). Gesperrte Talente aus dem
  // Talentspeicher bleiben mit Namen sichtbar, tragen isLocked und lassen
  // sich nicht für eine Auswertung hinzufügen. Ohne Sperren bleibt die
  // Referenz gleich, legacy und preview sehen also genau das Bisherige.
  // Solange der Speicher nicht gezählt ist oder ein Talent der Liste noch im
  // nachladenden Cache fehlt, lädt das Modal weiter.
  const talentStorage = useTalentStorage({ fetch: 'auto' })
  const talentsWithLocks = useMemo(
    () => markLockedTalents(talents, talentStorage),
    [talents, talentStorage]
  )
  const isStoragePending = hasPendingTalents(talents, talentStorage)
  const lockedCount = isStoragePending
    ? 0
    : (talentsWithLocks || []).filter((talent) => talent?.isLocked).length

  const [selectedTalents, setSelectedTalents] = useState([])

  // Hide talents from the suggestions list that are already selected.
  // Gesperrte Talente werden nicht vorgeschlagen.
  const suggestedTalents =
    talentsWithLocks
      ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .filter((t) => !t.isLocked && !selectedTalents.some((p) => p.id === t.id))
      .slice(0, 2) || []

  useEffect(() => {
    if (!inviteTalentOpen) {
      setSelectedTalents([])
    }
  }, [inviteTalentOpen])

  const handleTalentAdd = (talent) => {
    if (talent?.isLocked) return
    if (!selectedTalents.some((person) => person.id === talent.id)) {
      setSelectedTalents([...selectedTalents, talent])
    }
  }

  const handleTalentRemove = (talentToRemove) => {
    setSelectedTalents(
      selectedTalents.filter((person) => person.id !== talentToRemove.id)
    )
  }

  const handleNewTalentAdd = () => {
    alert('TODO: Add new talent functionality')
  }

  const handleSubmit = () => {
    addTalents(selectedTalents.map((talent) => talent.id))
    setInviteTalentOpen(false)
  }

  const handleModalClose = (isOpen) => {
    if (!isOpen && selectedTalents.length > 0) {
      handleSubmit()
    } else {
      setInviteTalentOpen(false)
    }
  }

  return (
    <Modal
      open={inviteTalentOpen}
      setOpen={handleModalClose}
      borderRadius='rounded-xl'
      size='xl'
      hideCloseButton
      disableOverlayClose
    >
      <div className='min-h-fit'>
        {isLoadingTalents || isStoragePending ? (
          <div className='flex h-full items-center justify-center'>
            <p className='text-lg text-mediumGrey'>{t('common.loading')}</p>
          </div>
        ) : (
          <React.Fragment>
            <div className='flex flex-row items-center justify-between'>
              <h1 className='text-3xl font-bold'>
                {t('discover.invite_talent_modal.title')}
              </h1>
            </div>
            <div className='mt-6'>
              <Heading className={'mb-1'} level={5}>
                {t('discover.invite_talent_modal.select_talents')}
              </Heading>

              <TalentSearchCombobox
                talents={talentsWithLocks}
                selectedTalents={selectedTalents}
                onTalentAdd={handleTalentAdd}
                placeholder={t(
                  'discover.invite_talent_modal.select_talents_placeholder'
                )}
              />

              {lockedCount > 0 && (
                <p className='mt-2 flex items-center gap-x-1.5 text-sm text-gray-600'>
                  <LockClosedIcon
                    aria-hidden='true'
                    className='size-4 text-gray-400'
                  />
                  {t('pricing.talent_locked.compare_locked_hint', {
                    count: lockedCount
                  })}
                </p>
              )}

              <SelectedTalentsList
                selectedTalents={selectedTalents}
                onTalentRemove={handleTalentRemove}
              />
            </div>

            <TalentSuggestionsSection
              suggestedTalents={suggestedTalents}
              onTalentAdd={handleTalentAdd}
              onNewTalentAdd={handleNewTalentAdd}
              hasCompletedOnboarding={hasCompletedOnboarding}
              selectedTalents={selectedTalents}
            />
            <div className='mt-6 flex justify-end gap-3'>
              <Button.SecondaryXL
                text={t('common.cancel')}
                onClick={() => setInviteTalentOpen(false)}
              />
              <Button.PrimaryXL
                text={t('discover.invite_talent_modal.confirm_selection')}
                onClick={handleSubmit}
                disabled={selectedTalents.length === 0}
              />
            </div>
          </React.Fragment>
        )}
      </div>
    </Modal>
  )
}
